<script setup>
/* global axios */
// Conexões — gestão de instâncias de WhatsApp. As credenciais dos provedores
// são globais (definidas no Super Admin); aqui a conta só usa os provedores
// liberados a ela para criar/conectar instâncias.
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const { t } = useI18n();
const route = useRoute();
const accountId = route.params.accountId;
const base = `/api/v1/accounts/${accountId}/kanban/connections`;

const loading = ref(true);
const providers = ref([]);
const instances = ref([]);

const showCreate = ref(false);
const createForm = ref({ provider_id: '', instance_name: '' });
const creating = ref(false);

const showQR = ref(false);
const qrInstance = ref(null);
const qrImage = ref('');
const qrStatus = ref('');
const qrError = ref('');
let qrTimer = null;

const STATUS_CLASS = {
  connected: 'bg-n-teal-3 text-n-teal-11',
  connecting: 'bg-n-amber-3 text-n-amber-11',
  disconnected: 'bg-n-alpha-2 text-n-slate-11',
};
const statusLabel = s => t(`CONNECTIONS.status.${s}`, s);
const providerLabel = inst =>
  inst.provider_name || `${inst.provider}`.toUpperCase();

const loadProviders = async () => {
  providers.value = (await axios.get(`${base}/providers`)).data.data || [];
  if (providers.value.length)
    createForm.value.provider_id = providers.value[0].id;
};
const loadInstances = async () => {
  instances.value = (await axios.get(`${base}/instances`)).data.data || [];
};

const normalizeQr = raw => {
  if (!raw) return '';
  return raw.startsWith('data:') ? raw : `data:image/png;base64,${raw}`;
};

const stopQR = () => {
  if (qrTimer) clearInterval(qrTimer);
  qrTimer = null;
};
const pollQR = async () => {
  if (!qrInstance.value) return;
  try {
    const r = (
      await axios.get(`${base}/${qrInstance.value.instance_name}/qrcode`)
    ).data;
    if (r.error) qrError.value = r.error;
    qrImage.value = normalizeQr(r.qrcode);
    qrStatus.value = r.status?.state || r.status || '';
    if (/(open|connected)/i.test(qrStatus.value)) {
      stopQR();
      await loadInstances();
    }
  } catch (e) {
    qrError.value = e?.response?.data?.error || t('CONNECTIONS.createError');
  }
};
const openQR = async inst => {
  qrInstance.value = inst;
  qrImage.value = '';
  qrError.value = '';
  qrStatus.value = '';
  showQR.value = true;
  await pollQR();
  qrTimer = setInterval(pollQR, 4000);
};
const closeQR = () => {
  stopQR();
  showQR.value = false;
  qrInstance.value = null;
};

const createInstance = async () => {
  if (!createForm.value.instance_name.trim() || !createForm.value.provider_id)
    return;
  creating.value = true;
  try {
    const r = (await axios.post(`${base}/create_instance`, createForm.value))
      .data;
    showCreate.value = false;
    await loadInstances();
    const inst = r.data;
    createForm.value = {
      provider_id: providers.value[0]?.id || '',
      instance_name: '',
    };
    if (inst) {
      if (r.qrcode) {
        qrInstance.value = inst;
        qrImage.value = normalizeQr(r.qrcode);
        qrError.value = r.error || '';
        showQR.value = true;
        qrTimer = setInterval(pollQR, 4000);
      } else {
        openQR(inst);
      }
    }
  } catch (e) {
    window.alert(e?.response?.data?.error || t('CONNECTIONS.createError'));
  } finally {
    creating.value = false;
  }
};

const act = async (inst, action) => {
  if (action === 'destroy') {
    if (
      !window.confirm(
        t('CONNECTIONS.confirmDelete', { name: inst.instance_name })
      )
    )
      return;
    await axios.delete(`${base}/${inst.instance_name}`);
  } else {
    await axios.post(`${base}/${inst.instance_name}/${action}`);
  }
  await loadInstances();
};

onMounted(async () => {
  try {
    await Promise.all([loadProviders(), loadInstances()]);
  } finally {
    loading.value = false;
  }
});
onBeforeUnmount(stopQR);

const inputCls =
  'block w-full pl-3 pr-9 py-2 text-sm rounded-lg border-0 outline outline-1 outline-offset-[-1px] bg-n-alpha-black2 placeholder:text-n-slate-10 text-n-slate-12 outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand focus:ring-0';
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <header
      class="flex items-center justify-between gap-3 px-6 py-4 border-b border-n-weak"
    >
      <div class="flex items-center gap-3">
        <div
          class="flex items-center justify-center w-8 h-8 rounded-lg bg-n-brand"
        >
          <svg
            class="w-4 h-4 text-white"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h1 class="text-heading-1 text-n-slate-12">
          {{ t('CONNECTIONS.title') }}
        </h1>
      </div>
      <button
        v-if="providers.length"
        class="inline-flex items-center h-8 gap-2 px-3 text-sm font-medium text-white rounded-lg bg-n-brand hover:brightness-110"
        @click="showCreate = true"
      >
        <svg
          class="w-4 h-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path stroke-linecap="round" d="M12 4v16m8-8H4" />
        </svg>
        {{ t('CONNECTIONS.newInstance') }}
      </button>
    </header>

    <div class="flex-1 p-6 overflow-y-auto">
      <p v-if="loading" class="text-sm text-n-slate-10">
        {{ t('CONNECTIONS.loading') }}
      </p>

      <template v-else>
        <div
          v-if="!providers.length"
          class="max-w-lg py-10 mx-auto text-center"
        >
          <p class="mb-1 text-heading-2 text-n-slate-12">
            {{ t('CONNECTIONS.noProvidersTitle') }}
          </p>
          <p class="text-sm text-n-slate-11">
            {{ t('CONNECTIONS.noProvidersDesc') }}
          </p>
        </div>

        <template v-else>
          <p
            v-if="!instances.length"
            class="py-10 text-sm text-center text-n-slate-10"
          >
            {{ t('CONNECTIONS.noInstances') }}
          </p>
          <div v-else class="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div
              v-for="inst in instances"
              :key="inst.id"
              class="flex items-center gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-1"
            >
              <div class="min-w-0">
                <div class="font-medium truncate text-n-slate-12">
                  {{ inst.instance_name }}
                </div>
                <div class="text-xs text-n-slate-10">
                  {{ providerLabel(inst) }}
                </div>
              </div>
              <span
                class="px-2 py-0.5 text-[0.6875rem] font-medium rounded-full"
                :class="
                  STATUS_CLASS[inst.status] || 'bg-n-alpha-2 text-n-slate-11'
                "
                >{{ statusLabel(inst.status) }}</span
              >
              <div class="flex items-center gap-1 ml-auto">
                <button
                  class="p-1.5 rounded-md text-n-slate-11 hover:bg-n-alpha-2"
                  :title="t('CONNECTIONS.act.qr')"
                  @click="openQR(inst)"
                >
                  <svg
                    class="w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h2v2h-2zM18 14h2v2h-2zM14 18h2v2h-2zM18 18h2v2h-2z"
                    />
                  </svg>
                </button>
                <button
                  class="p-1.5 rounded-md text-n-slate-11 hover:bg-n-alpha-2"
                  :title="t('CONNECTIONS.act.restart')"
                  @click="act(inst, 'restart')"
                >
                  <svg
                    class="w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </button>
                <button
                  class="p-1.5 rounded-md text-n-amber-11 hover:bg-n-alpha-2"
                  :title="t('CONNECTIONS.act.logout')"
                  @click="act(inst, 'logout')"
                >
                  <svg
                    class="w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                </button>
                <button
                  class="p-1.5 rounded-md text-n-ruby-11 hover:bg-n-ruby-3"
                  :title="t('CONNECTIONS.act.delete')"
                  @click="act(inst, 'destroy')"
                >
                  <svg
                    class="w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </template>
      </template>
    </div>

    <!-- modal criar instância -->
    <div
      v-if="showCreate"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-alpha-black2"
      @click.self="showCreate = false"
    >
      <div
        class="w-full max-w-md overflow-hidden shadow-md rounded-xl bg-n-solid-2"
      >
        <div class="px-6 py-4 border-b border-n-weak">
          <h3 class="text-heading-2 text-n-slate-12">
            {{ t('CONNECTIONS.newInstance') }}
          </h3>
        </div>
        <div class="px-6 py-5 space-y-3">
          <div>
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('CONNECTIONS.provider')
            }}</label>
            <select v-model="createForm.provider_id" :class="inputCls">
              <option v-for="p in providers" :key="p.id" :value="p.id">
                {{ p.name }} ({{ p.provider_type }})
              </option>
            </select>
          </div>
          <div>
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('CONNECTIONS.instanceName')
            }}</label>
            <input
              v-model="createForm.instance_name"
              :class="inputCls"
              :placeholder="t('CONNECTIONS.instanceNamePlaceholder')"
            />
          </div>
        </div>
        <div class="flex justify-end gap-2 px-6 py-4 border-t border-n-weak">
          <button
            class="px-4 py-2 text-sm font-medium rounded-lg outline outline-1 outline-n-strong text-n-slate-11 hover:bg-n-slate-9/10"
            @click="showCreate = false"
          >
            {{ t('CONNECTIONS.cancel') }}
          </button>
          <button
            class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand hover:brightness-110 disabled:opacity-60"
            :disabled="creating || !createForm.instance_name.trim()"
            @click="createInstance"
          >
            {{ creating ? t('CONNECTIONS.creating') : t('CONNECTIONS.create') }}
          </button>
        </div>
      </div>
    </div>

    <!-- modal QR -->
    <div
      v-if="showQR"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-alpha-black2"
      @click.self="closeQR"
    >
      <div
        class="w-full max-w-sm overflow-hidden text-center shadow-md rounded-xl bg-n-solid-2"
      >
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
        >
          <h3 class="text-heading-2 text-n-slate-12">
            {{ t('CONNECTIONS.qrTitle') }}
          </h3>
          <button
            class="p-1 rounded-md text-n-slate-11 hover:bg-n-alpha-2"
            @click="closeQR"
          >
            <svg
              class="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path stroke-linecap="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div class="flex flex-col items-center gap-3 px-6 py-6">
          <p class="text-sm text-n-slate-11">
            {{ t('CONNECTIONS.qrHint') }}
          </p>
          <img
            v-if="qrImage"
            :src="qrImage"
            alt="QR"
            class="border rounded-lg w-60 h-60 border-n-weak"
          />
          <div
            v-else
            class="flex items-center justify-center border rounded-lg w-60 h-60 border-n-weak text-n-slate-10"
          >
            {{ qrError || t('CONNECTIONS.qrLoading') }}
          </div>
          <p v-if="qrStatus" class="text-xs text-n-slate-10">{{ qrStatus }}</p>
        </div>
      </div>
    </div>
  </div>
</template>
